import React, { useState, useEffect } from 'react'
import { makeApiCall } from '../utils/customerUtils'
import { useTranslation } from 'react-i18next'
import { SettingsPageTemplate, SettingsCard, InfoTip } from '../components/SettingsPageTemplate'

interface UserSmsSettings {
  id?: number
  user_id: number
  two_way_sms_enabled: boolean
  custom_phone_number?: string
  messaging_profile_id?: string
  is_active: boolean
  activated_at?: string
}

interface SmsSettingsData {
  settings: UserSmsSettings
  admin_default_sms_number: string | null
}

const SmsSettings: React.FC = () => {
  const { t } = useTranslation()
  const [settings, setSettings] = useState<UserSmsSettings | null>(null)
  const [adminDefaultNumber, setAdminDefaultNumber] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    fetchSettings()
  }, [])

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [success])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await makeApiCall('api/settings/sms', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch SMS settings')
      }

      const data: SmsSettingsData = await response.json()
      setSettings(data.settings)
      setAdminDefaultNumber(data.admin_default_sms_number)

    } catch (err) {
      console.error('Error fetching SMS settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleTwoWaySms = async (enabled: boolean) => {
    if (!settings) return

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      const response = await makeApiCall('api/settings/sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          two_way_sms_enabled: enabled,
          custom_phone_number: settings.custom_phone_number,
          messaging_profile_id: settings.messaging_profile_id
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to update settings')
      }

      // Update local state
      setSettings(prev => prev ? { ...prev, two_way_sms_enabled: enabled } : null)
      
      if (enabled) {
        setSuccess('✅ Two-way SMS activated! You can now receive SMS messages. Contact support to set up your custom phone number.')
      } else {
        setSuccess('✅ Two-way SMS disabled. You can still send automatic status updates using the default number.')
      }

    } catch (err) {
      console.error('Error updating SMS settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to update settings')
    } finally {
      setSaving(false)
    }
  }

  const messageState = error ? { type: 'error' as const, text: error } : 
                       success ? { type: 'success' as const, text: success } : null

  return (
    <SettingsPageTemplate
      title="SMS Settings"
      description="Configure SMS preferences and two-way messaging"
      message={messageState}
      loading={loading}
    >
      <div className="space-y-6">
          {/* Default SMS Number Info */}
          <InfoTip type="info">
            <strong>📱 Default SMS Number:</strong><br/>
            All outgoing automatic SMS (deal status updates) will be sent from: <br/>
            <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-sm">
              {adminDefaultNumber || 'Not configured'}
            </code><br/>
            This number is configured by the admin and used for all outgoing-only SMS notifications.
          </InfoTip>

          {/* Two-Way SMS Settings */}
          <SettingsCard title="🔄 Two-Way SMS Communication">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Enable two-way SMS to receive messages from customers and send personalized responses. 
                  When disabled, you can only send automatic status updates.
                </p>
                
                {settings?.two_way_sms_enabled && (
                  <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                    <p className="text-green-800 dark:text-green-200 text-sm">
                      ✅ Two-way SMS is activated! You can send and receive SMS messages.
                      {settings.activated_at && (
                        <span className="block mt-1">
                          Activated on: {new Date(settings.activated_at).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {!settings?.two_way_sms_enabled && (
                  <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700 rounded">
                    <p className="text-gray-700 dark:text-gray-300 text-sm">
                      📤 Two-way SMS is disabled. You can only send automatic status updates using the default number.
                      Enable this feature to send and receive personalized messages.
                    </p>
                  </div>
                )}
              </div>

              <div className="ml-6">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings?.two_way_sms_enabled || false}
                    onChange={(e) => handleToggleTwoWaySms(e.target.checked)}
                    disabled={saving}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900"></div>
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    {settings?.two_way_sms_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
            </div>

            {/* Phone Number Management Link */}
            {settings?.two_way_sms_enabled && (
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                  📱 Dedicated Phone Number
                </h4>
                <p className="text-blue-800 dark:text-blue-200 text-sm mb-3">
                  Purchase your own dedicated phone number for professional calls and SMS communications. 
                  Your customers will see your number as the caller ID.
                </p>
                <button
                  onClick={() => window.location.href = '/dashboard/settings/phone-numbers'}
                  className="text-sm px-3 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors"
                >
                  Manage Phone Numbers →
                </button>
              </div>
            )}
          </SettingsCard>

          {/* How it Works */}
          <InfoTip type="info">
            <strong>ℹ️ How SMS Works:</strong><br/>
            <br/>
            ✓ <strong>Automatic Status Updates:</strong> Always sent from the default number when deal statuses change. Available to all users regardless of two-way SMS setting.<br/>
            <br/>
            📞 <strong>Two-Way Communication:</strong> When enabled, you can send and receive personalized messages in the Conversations page. Requires activation and eventually a custom number.<br/>
            <br/>
            ⚠️ <strong>Incoming Messages:</strong> The default number does not accept incoming messages. Only custom numbers (when available) will support two-way communication.
          </InfoTip>
      </div>
    </SettingsPageTemplate>
  )
}

export default SmsSettings